/* eslint-disable default-param-last */
import { createSelector } from '@reduxjs/toolkit';

import { AUTHENTICATED_ACCOUNT, SITE_LINKS, UNAUTHENTICATED_ACCOUNT } from 'constants/categoryIds';

import { categoryNameToUrl } from 'utils/format';

export const getData = ({ taxonomy: { data } = {} } = {}) => data;
export const getTaxonomyLevel = state => state.taxonomy.level;

export const getIsTaxonomyLoaded = state => state.taxonomy.loaded;
export const getIsTaxonomyLoading = state => state.taxonomy.loading;

export const getFooterData = state => state.taxonomy.footer.data;
export const getIsFooterLoaded = state => !!state.taxonomy.footer.data;
export const getIsFooterLoading = state => state.taxonomy.footer.loading;

export const nameToUrl = (name = '') => categoryNameToUrl(name.toLowerCase());

const returnId = (state, id) => id;

const DEFAULT_CATEGORY = {};
export const getCategoryById = createSelector(
  [getData, returnId],
  (data = {}, id) => data[id] || DEFAULT_CATEGORY,
);

export const getCategoryNameById = createSelector(getCategoryById, ({ name } = {}) => name);

export const getCategoryBannerById = createSelector(getCategoryById, ({ banner } = {}) => banner);

// Memoized function factory for category URL resolution
export const createCategoryUrlResolver = createSelector([getData], taxonomyData => categoryId => {
  const category = taxonomyData?.[categoryId];
  if (!category) return '';
  const { name, url } = category;
  return url ?? nameToUrl(name);
});

export const getSubcategoriesById = createSelector(
  getCategoryById,
  ({ categoryIds: categories = [] }) => categories,
);

const getSubCategoriesPaths = (taxonomy, category, categoryIds = [], level) => {
  const { name: fatherName, id: fatherId, path } = category;

  let categoryRootPath = level === 1 ? `shop/browse/` : null;

  categoryRootPath = `${categoryRootPath || ''}${path || categoryNameToUrl(fatherName)}`;

  const subCategories = categoryIds.map(id => {
    const { name, shortName, url, emptyOfProducts, hiddenInNav } = taxonomy[id];
    return {
      name,
      path: url || `${categoryRootPath}/${shortName || categoryNameToUrl(name)}`.toLowerCase(),
      id,
      level,
      parent: level === 1 ? ['10051'] : ['10051', fatherId],
      emptyOfProducts,
      hiddenInNav,
    };
  });
  return subCategories;
};

export const getG1AndG2 = createSelector(
  [getData, state => getCategoryById(state, '10051')],
  (taxonomy = {}, groceries) => {
    const { categoryIds } = groceries;
    const categoriesG1 = getSubCategoriesPaths(taxonomy, groceries, categoryIds, 1);

    if (!categoriesG1 || !categoriesG1.length) return null;

    const categories = [...categoriesG1];

    categoriesG1?.forEach(category => {
      const { id } = category;
      const groceriesLevel2 = taxonomy[id];
      categories.push(...getSubCategoriesPaths(taxonomy, category, groceriesLevel2.categoryIds, 2));
    });

    const nonEmptyCategories = categories.filter(
      category => !category.emptyOfProducts && !category.hiddenInNav,
    );

    // TODO: this could be memoized to avoid recreating the array and unnecessary rerenders
    return nonEmptyCategories;
  },
);

export const getMenuCategoryById = createSelector([getCategoryById, getData], (category, data) => {
  const childCategoryIds = category.categoryIds ?? [];

  // Filter out child categories that are empty of products or hidden
  const categoryIds = childCategoryIds.filter(
    id => data[id] && !data[id]?.emptyOfProducts && !data[id]?.hiddenInNav,
  );

  // Decorate with child category objects
  const categories = categoryIds
    .map(id => data[id])
    .reduce(
      (acc, item) => ({
        ...acc,
        [item.id]: {
          ...item,
          hasDescendants: !!item.categoryIds?.length,
        },
      }),
      {},
    );

  // TODO: this could be memoized to avoid recreating the array and unnecessary rerenders
  return {
    ...category,
    categoryIds,
    categories,
  };
});

export const getAccountMenu = createSelector(
  [(state, isLoggedIn) => ({ state, isLoggedIn })],
  ({ state, isLoggedIn }) => {
    const categoryId = isLoggedIn ? AUTHENTICATED_ACCOUNT : UNAUTHENTICATED_ACCOUNT;

    return getCategoryById(state, categoryId);
  },
);

export const getSiteLinks = state => getCategoryById(state, SITE_LINKS);
